import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Box, Typography, Button } from '@mui/material'
import { AddCircle as AddCircleIcon } from '@mui/icons-material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { makeStyles } from '@mui/styles'

import BasicDataGrid from 'components/generic/datagrid'

import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'

const useStyles = makeStyles(theme => ({
  root: {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
  },
}))

const SettingsGridWithBlade = ({
  title,
  subTitle,
  subTitle2,
  rowType,
  generateColumns,
  path,
  fields,
  dataGridProps,
  bladeTitle,
  bladeComponents,
  bladeActionOnSuccess,
  fieldsToWatch,
  setHasEditedGrid,
  apiRefDict,
  setApiRefDict,
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const apiRef = useGridApiRef()
  const methods = useFormContext()
  const { variables } = useSelector(state => state.featureFlags || {})

  const columns = generateColumns({ apiRef, control: methods.control, featureFlagVariables: variables })

  const onUpdateGrid = useCallback(() => {
    setHasEditedGrid(true)
    const newRows = Array.from(apiRef.current.getRowModels().values())
    methods.setValue(path, newRows)
  }, [apiRef])

  useEffect(() => {
    if (apiRef?.current?.subscribeEvent) {
      apiRef.current.subscribeEvent('rowsUpdate', () => {
        onUpdateGrid()
      })
    }
    if (!apiRefDict[path]) {
      setApiRefDict(prev => ({
        ...prev,
        [path]: apiRef,
      }))
    }
  }, [apiRef])

  const onClickOpenBlade = () => {
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.SETTINGS,
        formModalProps: {
          bladeTitle,
          bladeComponents,
          rowType,
          bladeActionOnSuccess,
          fieldsToWatch,
        },
      }),
    )
  }

  return (
    <>
      <Typography variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>
      {subTitle2 && (
        <Typography variant="body2" color="textSecondary">
          {subTitle2}
        </Typography>
      )}
      <Box mt={1}>
        <Button startIcon={<AddCircleIcon />} onClick={onClickOpenBlade}>
          {`Add ${rowType}`}
        </Button>
      </Box>
      <Box mt={1}>
        <BasicDataGrid
          className={classes.root}
          density="compact"
          height="auto"
          rows={fields}
          columns={columns}
          apiRef={apiRef}
          editMode="cell"
          disableColumnMenu
          hideFooter
          onCellClick={(_, event) => {
            event.stopPropagation()

            Object.values(apiRefDict).forEach(ref => {
              ref.current.state.focus.cell = null
            })
          }}
          {...dataGridProps}
        />
      </Box>
    </>
  )
}

SettingsGridWithBlade.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  subTitle2: PropTypes.string,
  rowType: PropTypes.string.isRequired,
  generateColumns: PropTypes.func,
  path: PropTypes.string.isRequired,
  fields: PropTypes.array,
  dataGridProps: PropTypes.object,
  bladeTitle: PropTypes.string.isRequired,
  bladeComponents: PropTypes.array,
  bladeActionOnSuccess: PropTypes.string.isRequired,
  fieldsToWatch: PropTypes.array,
  setHasEditedGrid: PropTypes.func,
  apiRefDict: PropTypes.object,
  setApiRefDict: PropTypes.func,
}

SettingsGridWithBlade.defaultProps = {
  subTitle2: '',
  generateColumns: () => [],
  dataGridProps: {},
  bladeComponents: [],
  fields: [],
  fieldsToWatch: [],
  setHasEditedGrid: () => {},
  apiRefDict: {},
  setApiRefDict: () => {},
}

export default SettingsGridWithBlade
